import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  RequestQuote as RequestQuoteIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext, enviarPayloadFacturaElectronica, emitirEcfDgii } from 'context';
import { FP, fpInputSx, fpMultilineInputSx } from 'components/financepro';
import VistaNotaCredito from './VistaNotaCredito';

/**
 * Diálogo para emitir Nota de Débito a partir de una factura.
 * Carga la factura completa, calcula impuestos según reglas del sistema y emite NCF.
 * Si la empresa emite comprobante electrónico, genera el Excel e-CF (E33) con QR.
 * El permiso de modificación se valida en la pantalla padre (Mantenimiento Nota de Débito).
 */
const NotaDebito = ({ open, onClose, facturaOrigen, puedeModificar = true }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, direccion_empresa: direccionEmpresa, nombreNegocio, rnc_empresa: rncEmpresa, comprobanteelectronico } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const [motivo, setMotivo] = useState('');
  const [monto, setMonto] = useState('0');
  const [guardando, setGuardando] = useState(false);
  const [cargandoFactura, setCargandoFactura] = useState(false);
  const [facturaCompleta, setFacturaCompleta] = useState(null);
  const [vistaNdAbierta, setVistaNdAbierta] = useState(false);
  const [datosVistaNd, setDatosVistaNd] = useState(null);
  const [reglasImpuestos, setReglasImpuestos] = useState([]);
  const [totalesNotaDebito, setTotalesNotaDebito] = useState({
    impuestos: [],
    totalImpuestos: 0,
    totalDescuentos: 0,
    total: 0,
  });
  const [calculandoImpuestosNd, setCalculandoImpuestosNd] = useState(false);
  const [fiscalListo, setFiscalListo] = useState(true);

  const cargarFacturaCompleta = useCallback(async () => {
    if (!facturaOrigen || !selectedCompany?.db_empresa) return;
    setCargandoFactura(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerfactura', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          nfact: facturaOrigen.nfact,
          tipofactura: facturaOrigen.tipofactura,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setFacturaCompleta(data.factura || {});
      } else {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'Error al cargar la factura');
      }
    } catch {
      showError('Error de conexión al cargar la factura');
    } finally {
      setCargandoFactura(false);
    }
  }, [facturaOrigen, selectedCompany, usuarioLog, claveLog, servidor, showError]);

  useEffect(() => {
    if (!open) {
      setVistaNdAbierta(false);
      setDatosVistaNd(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && facturaOrigen?.nfact != null && facturaOrigen?.tipofactura) {
      setMotivo('');
      setMonto('0');
      setFacturaCompleta(null);
      cargarFacturaCompleta();
    }
  }, [open, facturaOrigen?.nfact, facturaOrigen?.tipofactura, cargarFacturaCompleta]);

  useEffect(() => {
    if (!open || !selectedCompany?.db_empresa) return;
    let cancelled = false;
    fetch(servidor + '/api/facturabackend_obtenerreglasimpuestos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && !data.Error) setReglasImpuestos(data.reglas || []);
      })
      .catch(() => {
        if (!cancelled) setReglasImpuestos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedCompany, usuarioLog, claveLog, servidor]);

  const ncfFacturaOrigen =
    (facturaCompleta && facturaCompleta.ncf) || facturaOrigen?.ncf || '';

  const totalFactura = facturaCompleta
    ? parseFloat(facturaCompleta.total) || 0
    : parseFloat(facturaOrigen?.total) || 0;

  /** Nombre de impuesto que deben llevar las líneas para que `calcularimpuestos` las empareje con `reglas_impuestos.nombre`. */
  const nombreImpuestoParaReglas = useMemo(() => {
    const activas = (reglasImpuestos || [])
      .filter((r) => r.activa === 'S')
      .sort((a, b) => (parseFloat(a.orden) || 0) - (parseFloat(b.orden) || 0));
    if (activas.length > 0) return activas[0].nombre;
    return facturaCompleta?.impuestos?.[0]?.nombre || 'ITBIS';
  }, [reglasImpuestos, facturaCompleta?.impuestos]);

  /** Tasa (%) del impuesto aplicable, usada como `tasaitbis` en el payload del e-CF. */
  const tasaImpuestoNd = useMemo(() => {
    const imp = (totalesNotaDebito.impuestos || [])[0];
    if (imp && parseFloat(imp.porcentaje) > 0) return parseFloat(imp.porcentaje);
    const activa = (reglasImpuestos || []).find(
      (r) => r.activa === 'S' && parseFloat(r.porcentaje) > 0
    );
    return activa ? parseFloat(activa.porcentaje) : 0;
  }, [totalesNotaDebito.impuestos, reglasImpuestos]);

  const subtotalNd = useMemo(() => Math.max(0, parseFloat(monto) || 0), [monto]);

  useEffect(() => {
    if (!open || !selectedCompany?.db_empresa || cargandoFactura) {
      setTotalesNotaDebito({ impuestos: [], totalImpuestos: 0, totalDescuentos: 0, total: 0 });
      setFiscalListo(!open || subtotalNd <= 0);
      return;
    }
    if (subtotalNd <= 0) {
      setTotalesNotaDebito({ impuestos: [], totalImpuestos: 0, totalDescuentos: 0, total: 0 });
      setFiscalListo(true);
      return;
    }

    setFiscalListo(false);
    const itemsfactura = [
      {
        codigo: 'MONTO',
        descripcion: 'Nota de débito por monto',
        cantidad: 1,
        precio: subtotalNd,
        descuento: 0,
        subtotal: subtotalNd,
        descuento_general: 0,
        nombreimpuesto: nombreImpuestoParaReglas,
        tipoproducto: 'item',
      },
    ];

    let cancelled = false;
    setCalculandoImpuestosNd(true);
    fetch(servidor + '/api/facturabackend_calcularimpuestos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        subtotal: subtotalNd,
        itemsfactura,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || data.Error) {
          if (!cancelled && data.Error) {
            setTotalesNotaDebito({ impuestos: [], totalImpuestos: 0, totalDescuentos: 0, total: subtotalNd });
            setFiscalListo(true);
          }
          return;
        }
        const tImp = parseFloat(data.total_impuestos) || 0;
        const tDesc = parseFloat(data.total_descuentos) || 0;
        const total =
          data.total != null && !Number.isNaN(parseFloat(data.total))
            ? parseFloat(data.total)
            : subtotalNd + tImp - tDesc;
        const impArr = Array.isArray(data.impuestos)
          ? data.impuestos.map((imp) => ({
              id: imp.id || imp.codigo || imp.nombre,
              nombre: imp.nombre || imp.descripcion || 'Impuesto',
              porcentaje: parseFloat(imp.porcentaje) || 0,
              monto: parseFloat(imp.monto) || 0,
            }))
          : [];
        setTotalesNotaDebito({
          impuestos: impArr,
          totalImpuestos: tImp,
          totalDescuentos: tDesc,
          total: Math.round(total * 100) / 100,
        });
        setFiscalListo(true);
      })
      .catch(() => {
        if (!cancelled) {
          setTotalesNotaDebito({ impuestos: [], totalImpuestos: 0, totalDescuentos: 0, total: subtotalNd });
          setFiscalListo(true);
        }
      })
      .finally(() => {
        if (!cancelled) setCalculandoImpuestosNd(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    selectedCompany,
    usuarioLog,
    claveLog,
    servidor,
    cargandoFactura,
    subtotalNd,
    nombreImpuestoParaReglas,
  ]);

  const totalNotaConFiscal = Math.max(0, totalesNotaDebito.total || subtotalNd);

  // Descarga en el navegador el Excel del e-CF devuelto por el endpoint de la DGII.
  const descargarArchivoECF = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'notadebito_ecf.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleGuardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para emitir notas de débito.');
      return;
    }
    const estadoEcf = String(facturaOrigen?.ecf_estado || '').toLowerCase().trim();
    if (estadoEcf === 'rechazado' || estadoEcf === 'error') {
      showError(
        estadoEcf === 'rechazado'
          ? 'No se puede emitir nota de débito: el e-CF fue rechazado por la DGII.'
          : 'No se puede emitir nota de débito: el e-CF tiene error.'
      );
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    const montoNum = parseFloat(monto) || 0;
    if (montoNum <= 0) {
      showError('El monto debe ser mayor que cero');
      return;
    }
    if (!(motivo || '').trim()) {
      showError('Indique el motivo de la nota de débito');
      return;
    }

    setGuardando(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_guardarnotadebito', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          nfact_origen: facturaOrigen.nfact,
          tipofactura_origen: facturaOrigen.tipofactura,
          ncf_factura_origen: ncfFacturaOrigen,
          motivo: motivo.trim(),
          monto: montoNum,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok && !data.Error) {
        showSuccess(data.mensaje || 'Nota de débito registrada correctamente');
        const ncfNota = (data.ncf || '').trim();
        const observacionesFull = [motivo.trim(), ncfFacturaOrigen ? `Factura origen NCF: ${ncfFacturaOrigen}` : '']
          .filter(Boolean)
          .join(' | ');

        // Comprobante fiscal electrónico (e-CF): si la empresa emite comprobante
        // electrónico y la nota lleva un NCF electrónico (E33), se envía el detalle
        // al endpoint de la DGII para generar el Excel del e-CF (referenciando el
        // NCF de la factura de origen como comprobante modificado). El backend
        // devuelve la fecha de validez del NCF y la URL del QR.
        // En histresu del POS, `suplidor` es el código del cliente (que suele ser
        // el RNC/cédula); el nombre real viene de maestcta vía `nombre_cliente`.
        const docCliente = (facturaCompleta?.rnc || facturaOrigen?.suplidor || '').trim();
        const docLimpio = docCliente.replace(/\D/g, '');
        const nombreCliente = (facturaCompleta?.nombre_cliente || facturaOrigen?.nombre_cliente || '').trim();
        const esElectronica = comprobanteelectronico === 'S' && ncfNota.slice(0, 1).toUpperCase() === 'E';
        const codigocomprobante =
          ncfNota.slice(0, 1).toUpperCase() === 'E' && /^\d{2}$/.test(ncfNota.slice(1, 3))
            ? ncfNota.slice(1, 3)
            : '';
        const codigoComprobanteOrigen =
          ncfFacturaOrigen.slice(0, 1).toUpperCase() === 'E' && /^\d{2}$/.test(ncfFacturaOrigen.slice(1, 3))
            ? ncfFacturaOrigen.slice(1, 3)
            : '';
        const origenEsConsumo = codigoComprobanteOrigen === '32';
        const rncComprador = origenEsConsumo
          ? (docLimpio && docLimpio !== '999999' ? docLimpio : '')
          : (docLimpio.length === 9 || docLimpio.length === 11 ? docLimpio : '');
        let fechaValidaNd = '';
        let qrUrlNd = '';
        let fechaFirmaNd = '';
        let codigoSeguridadNd = '';
        if (esElectronica) {
          if (!ncfFacturaOrigen) {
            showError('La factura de origen no tiene NCF asignado. No se puede emitir el e-CF de la nota de débito.');
            setGuardando(false);
            return;
          }
          if (!origenEsConsumo && !rncComprador) {
            showError('El cliente no tiene un RNC o cédula válido (9 o 11 dígitos). Se requiere para emitir nota de débito electrónica de crédito fiscal.');
            setGuardando(false);
            return;
          }
          const ecfPayload = {
            comprobanteelectronico,
            rnc: rncComprador,
            razonsocial: nombreCliente,
            comprobante: ncfNota,
            tipocomprobante: 'Nota de Debito Electronica:E33',
            codigocomprobante,
            usuario: usuarioLog || '',
            password: claveLog || '',
            ncfmodificado: ncfFacturaOrigen,
            // FechaNCFModificado (obligatoria en el XML de la nota): fecha de la
            // factura de origen en formato dd-mm-aaaa.
            fechancfmodificado: (() => {
              const ff = (facturaCompleta?.fecha_firma || '').trim();
              if (ff) {
                const mFirma = ff.match(/^(\d{2}-\d{2}-\d{4})/);
                if (mFirma) return mFirma[1];
              }
              const f = String(facturaCompleta?.fecha || '').slice(0, 10);
              const m = f.match(/^(\d{4})-(\d{2})-(\d{2})/);
              return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
            })(),
            // Código de modificación DGII: 3 (corrige montos — un débito agrega
            // cargos, nunca anula). Verificado contra certecf: los consumos E32
            // enviados (RFCE o completos) usan códigos normales; el 5 aplica solo
            // a facturas de consumo que nunca se remitieron a la DGII.
            codigomodificacion: '3',
            razonmodificacion: motivo.trim().slice(0, 90),
            items: [
              {
                item: 'Nota de débito por monto',
                cantidad: 1,
                valor: subtotalNd,
                descuento: 0,
                tasaitbis: (totalesNotaDebito.totalImpuestos || 0) > 0 ? tasaImpuestoNd : 0,
              },
            ],
          };
          const ecf = await enviarPayloadFacturaElectronica(servidor, ecfPayload);
          if (ecf.ok) {
            descargarArchivoECF(ecf.blob, ecf.filename);
            fechaValidaNd = ecf.fechaValida || '';
            qrUrlNd = ecf.qrUrl || '';
          } else {
            showError(ecf.error || 'No se pudo generar el comprobante electrónico');
          }

          // Emisión real del e-CF a la DGII (firma + registro + envío), con la
          // referencia al NCF modificado. La nota nunca se bloquea: si la DGII
          // está caída queda pendiente de reenvío.
          const emision = await emitirEcfDgii(servidor, {
            ...ecfPayload,
            rncemisor: rncEmpresa || '',
            razonsocialemisor: typeof nombreNegocio === 'string' ? nombreNegocio : '',
            direccionemisor: direccionEmpresa || '',
          });
          if (emision.ok) {
            descargarArchivoECF(
              new Blob([emision.xml], { type: 'text/xml' }),
              `${emision.encf || 'ecf'}.xml`
            );
            fechaFirmaNd = emision.fecha_firma || '';
            codigoSeguridadNd = emision.codigo_seguridad || '';
            const etiquetas = {
              aceptado: 'aceptada por la DGII',
              aceptado_condicional: 'aceptada condicional por la DGII',
              en_proceso: `en proceso en la DGII${emision.track_id ? ` (TrackId ${emision.track_id})` : ''}`,
              rechazado: 'RECHAZADA por la DGII',
              pendiente_envio: 'firmada; DGII inaccesible, se reenviará',
              error: 'firmada; el envío a la DGII falló',
            };
            const detalleDgii = (emision.respuesta_dgii?.mensajes || [])
              .map((m) => `${m.valor || ''}${m.codigo ? ` (${m.codigo})` : ''}`)
              .filter((t) => t.trim())
              .join(' · ');
            const mensajeDgii =
              `e-CF ${emision.encf} ${etiquetas[emision.estado] || emision.estado}` +
              (detalleDgii ? ` — DGII: ${detalleDgii}` : '');
            if (emision.estado === 'rechazado') {
              showError(mensajeDgii);
            } else {
              showSuccess(mensajeDgii);
            }
          } else {
            showError(emision.error || 'No se pudo emitir el e-CF de la nota');
          }
        }

        setDatosVistaNd({
          items: [
            {
              codigo: 'MONTO',
              descripcion: 'Cargo por monto',
              cantidad: 1,
              precio: subtotalNd,
              descuento: 0,
              subtotal: subtotalNd,
              impuesto: totalesNotaDebito.totalImpuestos || 0,
            },
          ],
          impuestos: totalesNotaDebito.impuestos || [],
          subtotal: subtotalNd,
          totalDescuentos: totalesNotaDebito.totalDescuentos || 0,
          totalImpuestos: totalesNotaDebito.totalImpuestos || 0,
          total: totalNotaConFiscal,
          documento: 'Nota de Debito',
          numero: String(data.ncred ?? ''),
          fecha: new Date().toISOString().split('T')[0],
          ncf: ncfNota,
          tipoDocumento: 'Nota de Débito',
          tipo_documento: 'Nota de Debito',
          observaciones: observacionesFull,
          empresa: {
            nombre: nombreNegocio,
            direccion: direccionEmpresa,
            rnc: rncEmpresa,
          },
          cliente_nombre: nombreCliente || (facturaOrigen?.suplidor || '').trim(),
          id_empresa: selectedCompany,
          ncf_factura_origen: ncfFacturaOrigen,
          nfact_origen: facturaOrigen.nfact,
          tipofactura_origen: facturaOrigen.tipofactura,
          // Datos del comprobante fiscal electrónico para el QR y la fecha de validez.
          comprobanteelectronico: esElectronica ? 'S' : 'N',
          codigocomprobante,
          fechavencimiento: fechaValidaNd,
          rnc: rncComprador,
          qrUrl: qrUrlNd,
          fecha_firma: fechaFirmaNd,
          codigo_seguridad: codigoSeguridadNd,
        });
        setVistaNdAbierta(true);
      } else {
        showError(data.Error || 'No se pudo guardar la nota de débito.');
      }
    } catch {
      showError('Error de conexión al guardar la nota de débito');
    } finally {
      setGuardando(false);
    }
  };

  const cerrarVistaNd = () => {
    setVistaNdAbierta(false);
    setDatosVistaNd(null);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open && !vistaNdAbierta}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.warningSoft,
              color: FP.color.warning,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RequestQuoteIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            Emitir nota de débito
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          {!facturaOrigen ? (
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                border: `1px solid ${FP.color.border}`,
                bgcolor: FP.color.infoSoft,
              }}
            >
              Seleccione una factura para emitir una nota de débito.
            </Alert>
          ) : cargandoFactura ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  mb: 2.5,
                  p: 2,
                  bgcolor: FP.color.surfaceAlt,
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: FP.color.textSecondary,
                    mb: 0.5,
                  }}
                >
                  Factura de origen
                </Typography>
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 0.5 }}>
                  {facturaOrigen.tipofactura} · Nº {facturaOrigen.nfact}
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                  Cliente: {facturaOrigen.suplidor || '—'}
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                  Total factura:{' '}
                  <strong>
                    ${totalFactura.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </strong>
                </Typography>
                {ncfFacturaOrigen && (
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.88rem' }}>
                    NCF factura: <strong>{ncfFacturaOrigen}</strong>
                  </Typography>
                )}
              </Box>

              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border: `1px solid ${FP.color.border}`,
                  bgcolor: FP.color.infoSoft,
                }}
                icon={false}
              >
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.9rem' }}>
                  Impuestos (reglas del sistema)
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                  {reglasImpuestos.filter((r) => r.activa === 'S').length} regla(s) activa(s)
                  {nombreImpuestoParaReglas ? ` · Imputación: «${nombreImpuestoParaReglas}»` : ''}
                  {calculandoImpuestosNd && subtotalNd > 0 ? ' · Calculando…' : ''}
                </Typography>
              </Alert>

              {subtotalNd > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${FP.color.border}`,
                    bgcolor: FP.color.surfaceAlt,
                  }}
                >
                  <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.9rem' }}>
                    Subtotal nota: <strong>${subtotalNd.toFixed(2)}</strong>
                  </Typography>
                  {(totalesNotaDebito.impuestos || []).map((imp) => (
                    <Typography
                      key={String(imp.id ?? imp.nombre)}
                      sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}
                    >
                      {imp.nombre} ({imp.porcentaje}%): ${(parseFloat(imp.monto) || 0).toFixed(2)}
                    </Typography>
                  ))}
                  {(totalesNotaDebito.totalDescuentos || 0) > 0 && (
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                      Ajustes fiscales (restar): −${(totalesNotaDebito.totalDescuentos || 0).toFixed(2)}
                    </Typography>
                  )}
                  <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, mt: 0.5 }}>
                    Total nota de débito: ${totalNotaConFiscal.toFixed(2)}
                  </Typography>
                </Paper>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Motivo"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder="Ej: Intereses mora, gastos adicionales..."
                    disabled={!puedeModificar || !usuarioLog}
                    InputLabelProps={{ shrink: true }}
                    sx={fpMultilineInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monto (nota de débito)"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={monto}
                    onChange={(e) => setMonto(e.target.value)}
                    helperText="Monto adicional a cobrar al cliente (incluye impuestos arriba)"
                    disabled={!puedeModificar || !usuarioLog}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={onClose}
            startIcon={<CloseIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cerrar
          </Button>
          {facturaOrigen && !cargandoFactura && (
            <Button
              variant="contained"
              onClick={handleGuardar}
              disabled={
                guardando ||
                !fiscalListo ||
                (subtotalNd > 0 && calculandoImpuestosNd) ||
                !puedeModificar ||
                !usuarioLog
              }
              startIcon={guardando ? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} /> : <SaveIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: 1.4,
                fontWeight: 700,
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              {guardando ? 'Guardando…' : 'Guardar nota de débito'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {datosVistaNd && (
        <VistaNotaCredito open={vistaNdAbierta} onClose={cerrarVistaNd} notaCreditoData={datosVistaNd} />
      )}
    </>
  );
};

export default NotaDebito;
